import { format, parseISO } from "date-fns";
import { DATE_FORMAT } from "../constants";

export const ACCEPTED_FILE_TYPES =
  ".csv, .doc, .docx, .pdf, .ppt, .pptx, .xls, .xlsx, .txt";

const sortByName = (a, b) => {
  if (a.name < b.name) {
    return -1;
  }
  if (b.name < a.name) {
    return 1;
  }
  return 0;
};

export enum DECISION {
  GRANTED_IN_DRAFT = "GRANTED_IN_DRAFT",
  NOT_GRANTED_IN_DRAFT = "NOT_GRANTED_IN_DRAFT",
  GRANTED = "GRANTED",
  NOT_GRANTED = "NOT_GRANTED",
  APPROVED = "APPROVED",
  NOT_APPROVED = "NOT_APPROVED",
  REVOKED_IN_DRAFT = "REVOKED_IN_DRAFT",
  REVOKED = "REVOKED",
  VARIED_IN_DRAFT = "VARIED_IN_DRAFT",
}

export const findDecisionEnum = (key: string) =>
  DECISION[key as keyof typeof DECISION];

export const decisionEquals = (key: string, decision: DECISION) =>
  decision.toString() === key;

export enum STATE {
  NOT_IMPLEMENTED = "NOT_IMPLEMENTED",
  NEW_APPLICATION = "NEW_APPLICATION",
  APPLICATION_REVIEW = "APPLICATION_REVIEW",
  ESTIMATION_SCOPE_AND_COST_ESTIMATION = "ESTIMATION_SCOPE_AND_COST_ESTIMATION",
  ESTIMATION_ESTIMATE_REVIEW = "ESTIMATION_ESTIMATE_REVIEW",
  ESTIMATION_ESTIMATE_APPROVAL = "ESTIMATION_ESTIMATE_APPROVAL",
  ESTIMATION_ESTIMATE_ACCEPTANCE = "ESTIMATION_ESTIMATE_ACCEPTANCE",
  ASSESSMENT_RESOURCE_ALLOCATION = "ASSESSMENT_RESOURCE_ALLOCATION",
  ASSESSMENT_ENGINEERING_ASSESSMENT = "ASSESSMENT_ENGINEERING_ASSESSMENT",
  ASSESSMENT_REPORT_REVIEW = "ASSESSMENT_REPORT_REVIEW",
  ASSESSMENT_REPORT_APPROVAL = "ASSESSMENT_REPORT_APPROVAL",
  ASSESSMENT_REPORT_FINALISATION = "ASSESSMENT_REPORT_FINALISATION",
  ASSESSMENT_DRAFT_DECISION = "ASSESSMENT_DRAFT_DECISION",
  CONSULTATION_UNDER_CONSULTATION = "CONSULTATION_UNDER_CONSULTATION",
  CONSULTATION_SUBMISSION_REVIEW = "CONSULTATION_SUBMISSION_REVIEW",
  REASSESSMENT_RESOURCE_ALLOCATION = "REASSESSMENT_RESOURCE_ALLOCATION",
  REASSESSMENT_ENGINEERING_ASSESSMENT = "REASSESSMENT_ENGINEERING_ASSESSMENT",
  REASSESSMENT_REPORT_REVIEW = "REASSESSMENT_REPORT_REVIEW",
  REASSESSMENT_REPORT_APPROVAL = "REASSESSMENT_REPORT_APPROVAL",
  REASSESSMENT_REPORT_FINALISATION = "REASSESSMENT_REPORT_FINALISATION",
  FINALISATION_SUBMISSION_RESPONSE = "FINALISATION_SUBMISSION_RESPONSE",
  FINALISATION_FINAL_DECISION = "FINALISATION_FINAL_DECISION",
  FINALISATION_TOOLS_AND_SYSTEMS = "FINALISATION_TOOLS_AND_SYSTEMS",
  FINALISATION_INVOICING = "FINALISATION_INVOICING",
  FINALISATION_COMPLETE = "FINALISATION_COMPLETE",
  AMENDMENT_AMENDMENT_REASON = "AMENDMENT_AMENDMENT_REASON",
  AMENDMENT_AMENDMENT_REVIEW = "AMENDMENT_AMENDMENT_REVIEW",
  CANCELLATION_CANCELLATION_REASON = "CANCELLATION_CANCELLATION_REASON",
  CANCELLATION_TOOLS_AND_SYSTEMS = "CANCELLATION_TOOLS_AND_SYSTEMS",
  CANCELLATION_COMPLETE = "CANCELLATION_COMPLETE",
  REVOCATION_REVOCATION_REASON = "REVOCATION_REVOCATION_REASON",
  REVOCATION_DRAFT_DECISION = "REVOCATION_DRAFT_DECISION",
  REVOCATION_UNDER_CONSULTATION = "REVOCATION_UNDER_CONSULTATION",
  REVOCATION_SUBMISSION_RESPONSE = "REVOCATION_SUBMISSION_RESPONSE",
  REVOCATION_FINAL_DECISION = "REVOCATION_FINAL_DECISION",
  REVOCATION_TOOLS_AND_SYSTEMS = "REVOCATION_TOOLS_AND_SYSTEMS",
  REVOCATION_COMPLETE = "REVOCATION_COMPLETE",
  WITHDRAWAL_WITHDRAWAL_REASON = "WITHDRAWAL_WITHDRAWAL_REASON",
  WITHDRAWAL_INVOICING = "WITHDRAWAL_INVOICING",
  WITHDRAWAL_COMPLETE = "WITHDRAWAL_COMPLETE",
  VARIATION_VARIATION_SO_REASON = "VARIATION_VARIATION_SO_REASON",
  VARIATION_VARIATION_AO_REASON = "VARIATION_VARIATION_AO_REASON",
  VARIATION_VARIATION_AO_REVIEW = "VARIATION_VARIATION_AO_REVIEW",
}

type StateType = {
  state: STATE;
  name: string;
};

export const STATES: StateType[] = [
  { state: STATE.NOT_IMPLEMENTED, name: "Not implemented" },
  {
    state: STATE.NEW_APPLICATION,
    name: "New application",
  },
  {
    state: STATE.APPLICATION_REVIEW,
    name: "Application review",
  },
  {
    state: STATE.ESTIMATION_SCOPE_AND_COST_ESTIMATION,
    name: "Scope and cost estimation",
  },
  {
    state: STATE.ESTIMATION_ESTIMATE_REVIEW,
    name: "Estimate review",
  },
  {
    state: STATE.ESTIMATION_ESTIMATE_APPROVAL,
    name: "Estimate approval",
  },
  {
    state: STATE.ESTIMATION_ESTIMATE_ACCEPTANCE,
    name: "Estimate acceptance",
  },
  {
    state: STATE.ASSESSMENT_RESOURCE_ALLOCATION,
    name: "Resource allocation",
  },
  {
    state: STATE.ASSESSMENT_ENGINEERING_ASSESSMENT,
    name: "Engineering assessment",
  },
  {
    state: STATE.ASSESSMENT_REPORT_REVIEW,
    name: "Report review",
  },
  {
    state: STATE.ASSESSMENT_REPORT_APPROVAL,
    name: "Report approval",
  },
  {
    state: STATE.ASSESSMENT_REPORT_FINALISATION,
    name: "Report finalisation",
  },
  {
    state: STATE.ASSESSMENT_DRAFT_DECISION,
    name: "Draft decision",
  },
  {
    state: STATE.CONSULTATION_UNDER_CONSULTATION,
    name: "Under consultation",
  },
  {
    state: STATE.CONSULTATION_SUBMISSION_REVIEW,
    name: "Submission review",
  },
  {
    state: STATE.REASSESSMENT_RESOURCE_ALLOCATION,
    name: "Resource allocation",
  },
  {
    state: STATE.REASSESSMENT_ENGINEERING_ASSESSMENT,
    name: "Engineering assessment",
  },
  {
    state: STATE.REASSESSMENT_REPORT_REVIEW,
    name: "Report review",
  },
  {
    state: STATE.REASSESSMENT_REPORT_APPROVAL,
    name: "Report approval",
  },
  {
    state: STATE.REASSESSMENT_REPORT_FINALISATION,
    name: "Report finalisation",
  },
  {
    state: STATE.FINALISATION_SUBMISSION_RESPONSE,
    name: "Submission response",
  },
  {
    state: STATE.FINALISATION_FINAL_DECISION,
    name: "Final decision",
  },
  {
    state: STATE.FINALISATION_TOOLS_AND_SYSTEMS,
    name: "Tools and systems",
  },
  {
    state: STATE.FINALISATION_INVOICING,
    name: "Invoicing",
  },
  {
    state: STATE.FINALISATION_COMPLETE,
    name: "Completed",
  },
  {
    state: STATE.AMENDMENT_AMENDMENT_REASON,
    name: "Amendment reason",
  },
  {
    state: STATE.AMENDMENT_AMENDMENT_REVIEW,
    name: "Amendment review",
  },
  {
    state: STATE.CANCELLATION_CANCELLATION_REASON,
    name: "Cancellation reason",
  },

  {
    state: STATE.CANCELLATION_TOOLS_AND_SYSTEMS,
    name: "Tools and systems",
  },
  {
    state: STATE.CANCELLATION_COMPLETE,
    name: "Completed",
  },
  {
    state: STATE.REVOCATION_REVOCATION_REASON,
    name: "Revocation reason",
  },
  {
    state: STATE.REVOCATION_DRAFT_DECISION,
    name: "Draft decision",
  },
  {
    state: STATE.REVOCATION_UNDER_CONSULTATION,
    name: "Under consultation",
  },
  {
    state: STATE.REVOCATION_SUBMISSION_RESPONSE,
    name: "Submission response",
  },
  {
    state: STATE.REVOCATION_FINAL_DECISION,
    name: "Final decision",
  },
  {
    state: STATE.REVOCATION_TOOLS_AND_SYSTEMS,
    name: "Tools and systems",
  },
  {
    state: STATE.REVOCATION_COMPLETE,
    name: "Completed",
  },
  {
    state: STATE.WITHDRAWAL_WITHDRAWAL_REASON,
    name: "Withdrawal reason",
  },
  {
    state: STATE.WITHDRAWAL_INVOICING,
    name: "Invoicing",
  },
  {
    state: STATE.WITHDRAWAL_COMPLETE,
    name: "Completed",
  },
  {
    state: STATE.VARIATION_VARIATION_SO_REASON,
    name: "Variation reason",
  },
  {
    state: STATE.VARIATION_VARIATION_AO_REASON,
    name: "Variation reason",
  },
  {
    state: STATE.VARIATION_VARIATION_AO_REVIEW,
    name: "Variation review",
  },
];

export const findStateEnum = (key: string) =>
  STATE[key as keyof typeof STATE] || STATE.NOT_IMPLEMENTED;

export const findState = (key: STATE) => {
  const state = findStateEnum(key);
  return STATES.find((entry) => state === entry.state);
};

export const stateEquals = (key: string, state: STATE) =>
  state.toString() === key;

export const isCompleted = (state) =>
  stateEquals(state, STATE.FINALISATION_COMPLETE) ||
  stateEquals(state, STATE.CANCELLATION_COMPLETE) ||
  stateEquals(state, STATE.REVOCATION_COMPLETE) ||
  stateEquals(state, STATE.WITHDRAWAL_COMPLETE);

export enum STATUS {
  APPLICATION_RECEIVED = "APPLICATION_RECEIVED",
  APPROVED = "APPROVED",
  CANCELLED = "CANCELLED",
  DRAFT = "DRAFT",
  EXPIRED = "EXPIRED",
  GRANTED = "GRANTED",
  GRANTED_IN_DRAFT = "GRANTED_IN_DRAFT",
  NOT_APPROVED = "NOT_APPROVED",
  NOT_GRANTED = "NOT_GRANTED",
  NOT_GRANTED_IN_DRAFT = "NOT_GRANTED_IN_DRAFT",
  PROCESSING = "PROCESSING",
  REVOKED = "REVOKED",
  REVOKED_IN_DRAFT = "REVOKED_IN_DRAFT",
  WITHDRAWN = "WITHDRAWN",
  VARIED_IN_DRAFT = "VARIED_IN_DRAFT",
}

type StatusType = {
  status: STATUS;
  name: string;
  // Used in the filter dropdown list if present
  choice?: string;
};

export const STATUSES: StatusType[] = [
  { status: STATUS.APPROVED, name: "Approved", choice: "Approved" },
  {
    status: STATUS.APPLICATION_RECEIVED,
    name: "Application received",
    choice: "Application received",
  },
  { status: STATUS.CANCELLED, name: "Cancelled", choice: "Cancelled" },
  { status: STATUS.DRAFT, name: "Draft", choice: "Draft" },
  {
    status: STATUS.EXPIRED,
    name: "Expired",
    choice: "Expired",
  },
  { status: STATUS.GRANTED, name: "Granted", choice: "Granted" },
  {
    status: STATUS.GRANTED_IN_DRAFT,
    name: "Granted in draft",
    choice: "Granted in draft",
  },
  {
    status: STATUS.NOT_APPROVED,
    name: "Not approved",
    choice: "Not approved",
  },
  {
    status: STATUS.NOT_GRANTED,
    name: "Not granted",
    choice: "Not granted",
  },
  {
    status: STATUS.NOT_GRANTED_IN_DRAFT,
    name: "Not granted in draft",
    choice: "Not granted in draft",
  },
  { status: STATUS.PROCESSING, name: "Processing", choice: "Processing" },
  { status: STATUS.REVOKED, name: "Revoked", choice: "Revoked" },
  {
    status: STATUS.REVOKED_IN_DRAFT,
    name: "Revoked in draft",
    choice: "Revoked in draft",
  },
  { status: STATUS.WITHDRAWN, name: "Withdrawn", choice: "Withdrawn" },
  {
    status: STATUS.VARIED_IN_DRAFT,
    name: "Varied in draft",
    choice: "Varied in draft",
  },
];

export const findStatusEnum = (key: string) =>
  STATUS[key as keyof typeof STATUS] || STATUS.DRAFT;

export const findStatus = (key: any) => {
  const status = findStatusEnum(key);
  return STATUSES.find((entry) => status === entry.status);
};

export enum PHASE {
  NEW_APPLICATION = "NEW_APPLICATION",
  ESTIMATION = "ESTIMATION",
  ASSESSMENT = "ASSESSMENT",
  CONSULTATION = "CONSULTATION",
  REASSESSMENT = "REASSESSMENT",
  FINALISATION = "FINALISATION",
  // Fake phase to show that Finalisation is completed
  COMPLETED = "COMPLETED",
  AMENDMENT = "AMENDMENT",
  CANCELLATION = "CANCELLATION",
  REVOCATION = "REVOCATION",
  REVOCATION_CONSULTATION = "REVOCATION_CONSULTATION",
  REVOCATION_FINALISATION = "REVOCATION_FINALISATION",
  WITHDRAWAL = "WITHDRAWAL",
  VARIATION = "VARIATION",
  // Fake phase to indicate no phase
  NONE = "NONE",
}

export type PhaseType = {
  phase: PHASE;
  index: number;
  name: string;
  numberOfSteps: number;
};

export const PHASES: PhaseType[] = [
  {
    phase: PHASE.NONE,
    index: 0,
    name: "None",
    numberOfSteps: 0,
  },
  {
    phase: PHASE.NEW_APPLICATION,
    index: 0,
    name: "New application",
    numberOfSteps: 2,
  },
  {
    phase: PHASE.ESTIMATION,
    index: 1,
    name: "Estimation",
    numberOfSteps: 4,
  },
  {
    phase: PHASE.ASSESSMENT,
    index: 2,
    name: "Assessment",
    numberOfSteps: 6,
  },
  {
    phase: PHASE.CONSULTATION,
    index: 3,
    name: "Consultation",
    numberOfSteps: 2,
  },
  {
    phase: PHASE.REASSESSMENT,
    index: 4,
    name: "Reassessment",
    numberOfSteps: 5,
  },
  {
    phase: PHASE.FINALISATION,
    index: 5,
    name: "Finalisation",
    numberOfSteps: 4,
  },
  {
    phase: PHASE.AMENDMENT,
    index: 0,
    name: "Amendment",
    numberOfSteps: 2,
  },
  {
    phase: PHASE.CANCELLATION,
    index: 0,
    name: "Cancellation",
    numberOfSteps: 3,
  },
  {
    phase: PHASE.REVOCATION,
    index: 0,
    name: "Revocation",
    numberOfSteps: 3,
  },
  {
    phase: PHASE.REVOCATION_CONSULTATION,
    index: 1,
    name: "Consultation",
    numberOfSteps: 2,
  },
  {
    phase: PHASE.REVOCATION_FINALISATION,
    index: 2,
    name: "Finalisation",
    numberOfSteps: 3,
  },
  {
    phase: PHASE.WITHDRAWAL,
    index: 0,
    name: "Withdrawal",
    numberOfSteps: 3,
  },
  {
    phase: PHASE.VARIATION,
    index: 0,
    name: "Variation",
    numberOfSteps: 2,
  },
  {
    phase: PHASE.COMPLETED,
    index: 6,
    name: "Completed",
    numberOfSteps: 0,
  },
];

export const findPhase = (phase: PHASE): PhaseType =>
  PHASES.find((entry) => phase === entry.phase) || PHASES[0];

export const findPhaseFromState = (state, applicationStateLookup) => {
  const workflowState = Object.keys(applicationStateLookup)
    .filter((key) => applicationStateLookup[key].state === state)
    .map((key) => applicationStateLookup[key])[0];
  return workflowState
    ? findPhase(workflowState.phase)?.name
    : findPhase(PHASE.NONE).name;
};

export enum TYPE {
  DISPENSATION = "DISPENSATION",
  EQUIVALENCE = "EQUIVALENCE",
}

type TypeType = {
  type: TYPE;
  name: string;
  // Used in the filter dropdown list if present
  choice: string;
};

export const TYPES: TypeType[] = [
  {
    type: TYPE.DISPENSATION,
    name: "Dispensation",
    choice: "Dispensation",
  },
  { type: TYPE.EQUIVALENCE, name: "Equivalence", choice: "Equivalence" },
];

export const findTypeEnum = (key: string) =>
  TYPE[key as keyof typeof TYPE] || TYPE.DISPENSATION;

export const findType = (key: TYPE) => {
  const type = findTypeEnum(key);
  return TYPES.find((entry) => type === entry.type);
};

export enum WORKFLOW {
  DISPENSATION = "DISPENSATION",
  EQUIVALENCE = "EQUIVALENCE",
  AMENDMENT = "AMENDMENT",
  CANCELLATION = "CANCELLATION",
  REESTIMATION = "REESTIMATION",
  REVOCATION = "REVOCATION",
  VARY_SO = "VARY_SO",
  VARY_AO = "VARY_AO",
  WITHDRAWAL = "WITHDRAWAL",
}

type WorkflowType = {
  workflow: WORKFLOW;
  name: string;
  action: string;
};

export const WORKFLOWS: WorkflowType[] = [
  {
    workflow: WORKFLOW.DISPENSATION,
    name: "Dispensation",
    action: "Application",
  },
  {
    workflow: WORKFLOW.EQUIVALENCE,
    name: "Equivalence",
    action: "Application",
  },
  { workflow: WORKFLOW.AMENDMENT, name: "Amendment", action: "Amended" },
  {
    workflow: WORKFLOW.CANCELLATION,
    name: "Cancellation",
    action: "Cancelled",
  },
  {
    workflow: WORKFLOW.REESTIMATION,
    name: "Scope or cost re-estimation",
    action: "Re-estimated",
  },
  { workflow: WORKFLOW.REVOCATION, name: "Revocation", action: "Revoked" },
  { workflow: WORKFLOW.VARY_SO, name: "Variation", action: "Varied" },
  { workflow: WORKFLOW.VARY_AO, name: "Variation", action: "Varied" },
  { workflow: WORKFLOW.WITHDRAWAL, name: "Withdrawal", action: "Withdrawn" },
];

export const findWorkflowEnum = (key: string) =>
  WORKFLOW[key as keyof typeof WORKFLOW] || WORKFLOW.DISPENSATION;

export const findWorkflow = (key: any) => {
  const workflow = findWorkflowEnum(key);
  return WORKFLOWS.find((entry) => workflow === entry.workflow);
};

export enum ROLE {
  GUEST = "GUEST",
  COMPLIANCE_COORDINATOR = "COMPLIANCE_COORDINATOR",
  ENGINEER = "ENGINEER",
  ESTIMATE_REVIEWER = "ESTIMATE_REVIEWER",
  PSG_MANAGER = "PSG_MANAGER",
  RESOURCE_MANAGER = "RESOURCE_MANAGER",
  TECHNICAL_REVIEWER = "TECHNICAL_REVIEWER",
}

type RoleType = {
  role: ROLE;
  name: string;
  mapping: string;
  // Used in the filter dropdown list if present
  choice?: string;
};

export const ROLES: RoleType[] = [
  {
    role: ROLE.GUEST,
    name: "Guest",
    mapping: "none",
  },
  {
    role: ROLE.COMPLIANCE_COORDINATOR,
    name: "Compliance Coordinator",
    mapping: "complianceCoordinator",
    choice: "Compliance Coordinator",
  },
  {
    role: ROLE.ENGINEER,
    name: "Engineer",
    mapping: "engineer",
    choice: "Engineer",
  },
  {
    role: ROLE.ESTIMATE_REVIEWER,
    name: "Estimate Reviewer",
    mapping: "estimateReviewer",
    choice: "Estimate Reviewer",
  },
  {
    role: ROLE.PSG_MANAGER,
    name: "PSG Manager",
    mapping: "psgManager",
    choice: "PSG Manager",
  },
  {
    role: ROLE.RESOURCE_MANAGER,
    name: "Resource Manager",
    mapping: "resourceManager",
    choice: "Resource Manager",
  },
  {
    role: ROLE.TECHNICAL_REVIEWER,
    name: "Technical Reviewer",
    mapping: "technicalReviewer",
    choice: "Technical Reviewer",
  },
];

export const findRole = (role: ROLE) =>
  ROLES.find((entry) => role === entry.role) || ROLES[0];

export const findRoleByMapping = (mapping: string) =>
  ROLES.find((entry) => mapping === entry.mapping) || ROLES[0];

export const includesRole = (roles: string[] = [], role: ROLE) =>
  roles.includes(role.toString());

export enum ACTION {
  SAVE = "SAVE",
  DISCARD = "DISCARD",
  ACCEPT = "ACCEPT",
  REJECT = "REJECT",
  GRANTED = "GRANTED",
  NOT_GRANTED = "NOT_GRANTED",
}

type ActionType = {
  action: ACTION;
  name: string;
  mapping: string;
};

export const ACTIONS: ActionType[] = [
  {
    action: ACTION.SAVE,
    name: "Save",
    mapping: "save",
  },
  {
    action: ACTION.DISCARD,
    name: "Discard",
    mapping: "discard",
  },
  {
    action: ACTION.ACCEPT,
    name: "Accept",
    mapping: "accept",
  },
  {
    action: ACTION.REJECT,
    name: "Reject",
    mapping: "reject",
  },
  {
    action: ACTION.GRANTED,
    name: "Granted",
    mapping: "granted",
  },
  {
    action: ACTION.ACCEPT,
    name: "Not Granted",
    mapping: "notGranted",
  },
];

export const findAction = (role: ACTION) =>
  ACTIONS.find((entry) => role === entry.action);

export const findActionByMapping = (mapping: string) =>
  ACTIONS.find((entry) => mapping === entry.mapping);

export const includesAction = (actions: string[] = [], action: ACTION) =>
  actions.includes(action.toString());

export const roleChoices = ROLES.filter((role) => role.choice).map((role) => ({
  id: role.role.toString(),
  name: role.choice,
}));
export const typeChoices = TYPES.map((type) => ({
  id: type.type.toString(),
  name: type.choice,
}));

export const stateChoices = [
  {
    name: "New application",
    id: STATE.NEW_APPLICATION,
  },
  {
    name: "Application review",
    id: STATE.APPLICATION_REVIEW,
  },
  {
    name: "Scope and cost estimation",
    id: STATE.ESTIMATION_SCOPE_AND_COST_ESTIMATION,
  },
  {
    name: "Estimate review",
    id: STATE.ESTIMATION_ESTIMATE_REVIEW,
  },
  {
    name: "Estimate approval",
    id: STATE.ESTIMATION_ESTIMATE_APPROVAL,
  },
  {
    name: "Estimate acceptance",
    id: STATE.ESTIMATION_ESTIMATE_ACCEPTANCE,
  },
  {
    name: "Resource allocation",
    id: [
      STATE.ASSESSMENT_RESOURCE_ALLOCATION,
      STATE.REASSESSMENT_RESOURCE_ALLOCATION,
    ],
  },
  {
    name: "Engineering assessment",
    id: [
      STATE.ASSESSMENT_ENGINEERING_ASSESSMENT,
      STATE.REASSESSMENT_ENGINEERING_ASSESSMENT,
    ],
  },
  {
    name: "Report review",
    id: [STATE.ASSESSMENT_REPORT_REVIEW, STATE.REASSESSMENT_REPORT_REVIEW],
  },
  {
    name: "Report approval",
    id: [STATE.ASSESSMENT_REPORT_APPROVAL, STATE.REASSESSMENT_REPORT_APPROVAL],
  },
  {
    name: "Report finalisation",
    id: [
      STATE.ASSESSMENT_REPORT_FINALISATION,
      STATE.REASSESSMENT_REPORT_FINALISATION,
    ],
  },
  {
    name: "Draft decision",
    id: [STATE.ASSESSMENT_DRAFT_DECISION, STATE.REVOCATION_DRAFT_DECISION],
  },
  {
    name: "Under consultation",
    id: [
      STATE.CONSULTATION_UNDER_CONSULTATION,
      STATE.REVOCATION_UNDER_CONSULTATION,
    ],
  },
  {
    name: "Submission review",
    id: [STATE.CONSULTATION_SUBMISSION_REVIEW],
  },
  {
    name: "Submission response",
    id: [
      STATE.FINALISATION_SUBMISSION_RESPONSE,
      STATE.REVOCATION_SUBMISSION_RESPONSE,
    ],
  },
  {
    name: "Final decision",
    id: [STATE.FINALISATION_FINAL_DECISION, STATE.REVOCATION_FINAL_DECISION],
  },
  {
    name: "Tools and systems",
    id: [
      STATE.FINALISATION_TOOLS_AND_SYSTEMS,
      STATE.CANCELLATION_TOOLS_AND_SYSTEMS,
      STATE.REVOCATION_TOOLS_AND_SYSTEMS,
    ],
  },
  {
    name: "Invoicing",
    id: [STATE.FINALISATION_INVOICING, STATE.WITHDRAWAL_INVOICING],
  },
  {
    name: "Completed",
    id: [
      STATE.FINALISATION_COMPLETE,
      STATE.CANCELLATION_COMPLETE,
      STATE.REVOCATION_COMPLETE,
      STATE.WITHDRAWAL_COMPLETE,
    ],
  },
  {
    name: "Amendment reason",
    id: STATE.AMENDMENT_AMENDMENT_REASON,
  },
  {
    name: "Amendment review",
    id: STATE.AMENDMENT_AMENDMENT_REVIEW,
  },
  {
    name: "Cancellation reason",
    id: STATE.CANCELLATION_CANCELLATION_REASON,
  },
  {
    name: "Revocation reason",
    id: STATE.REVOCATION_REVOCATION_REASON,
  },
  {
    name: "Withdrawal reason",
    id: STATE.WITHDRAWAL_WITHDRAWAL_REASON,
  },
  {
    name: "Variation reason",
    id: [
      STATE.VARIATION_VARIATION_SO_REASON,
      STATE.VARIATION_VARIATION_AO_REASON,
    ],
  },
  {
    name: "Variation review",
    id: STATE.VARIATION_VARIATION_AO_REVIEW,
  },
]
  .map((item) => {
    if (typeof item.id === "string") {
      return item;
    }
    return { ...item, id: `[${item?.id?.join(",")}]` };
  })
  .sort(sortByName);

export const statusChoices = STATUSES.filter((status) => status.choice).map(
  (status) => ({
    id: status.status.toString(),
    name: status.choice,
  })
);

export const completedChoices = [
  {
    name: "Completed",
    id: [
      STATUS.APPROVED,
      STATUS.CANCELLED,
      STATUS.GRANTED,
      STATUS.NOT_APPROVED,
      STATUS.NOT_GRANTED,
      STATUS.REVOKED,
      STATUS.WITHDRAWN,
    ],
  },
  {
    name: "In-progress",
    id: [
      STATUS.APPLICATION_RECEIVED,
      STATUS.DRAFT,
      STATUS.GRANTED_IN_DRAFT,
      STATUS.NOT_GRANTED_IN_DRAFT,
      STATUS.PROCESSING,
      STATUS.REVOKED_IN_DRAFT,
      STATUS.VARIED_IN_DRAFT,
    ],
  },
]
  .map((item) => {
    if (typeof item.id === "string") {
      return item;
    }
    return { ...item, id: `[${item?.id?.join(",")}]` };
  })
  .sort(sortByName);

export const phaseChoices = [
  {
    name: "New application",
    id: [STATE.NEW_APPLICATION, STATE.APPLICATION_REVIEW],
  },
  {
    name: "Estimation",
    id: [
      STATE.ESTIMATION_SCOPE_AND_COST_ESTIMATION,
      STATE.ESTIMATION_ESTIMATE_REVIEW,
      STATE.ESTIMATION_ESTIMATE_APPROVAL,
      STATE.ESTIMATION_ESTIMATE_ACCEPTANCE,
    ],
  },
  {
    name: "Assessment",
    id: [
      STATE.ASSESSMENT_RESOURCE_ALLOCATION,
      STATE.ASSESSMENT_ENGINEERING_ASSESSMENT,
      STATE.ASSESSMENT_REPORT_REVIEW,
      STATE.ASSESSMENT_REPORT_APPROVAL,
      STATE.ASSESSMENT_REPORT_FINALISATION,
      STATE.ASSESSMENT_DRAFT_DECISION,
    ],
  },
  {
    name: "Consultation",
    id: [
      STATE.CONSULTATION_UNDER_CONSULTATION,
      STATE.CONSULTATION_SUBMISSION_REVIEW,
      STATE.REVOCATION_UNDER_CONSULTATION,
    ],
  },
  {
    name: "Reassessment",
    id: [
      STATE.REASSESSMENT_RESOURCE_ALLOCATION,
      STATE.REASSESSMENT_ENGINEERING_ASSESSMENT,
      STATE.REASSESSMENT_REPORT_REVIEW,
      STATE.REASSESSMENT_REPORT_APPROVAL,
      STATE.REASSESSMENT_REPORT_FINALISATION,
    ],
  },
  {
    name: "Finalisation",
    id: [
      STATE.FINALISATION_SUBMISSION_RESPONSE,
      STATE.FINALISATION_FINAL_DECISION,
      STATE.FINALISATION_TOOLS_AND_SYSTEMS,
      STATE.FINALISATION_INVOICING,
      STATE.FINALISATION_COMPLETE,
      STATE.REVOCATION_SUBMISSION_RESPONSE,
      STATE.REVOCATION_FINAL_DECISION,
      STATE.REVOCATION_TOOLS_AND_SYSTEMS,
      STATE.REVOCATION_COMPLETE,
    ],
  },
  {
    name: "Amendment",
    id: [STATE.AMENDMENT_AMENDMENT_REASON, STATE.AMENDMENT_AMENDMENT_REVIEW],
  },
  {
    name: "Cancellation",
    id: [
      STATE.CANCELLATION_CANCELLATION_REASON,
      STATE.CANCELLATION_TOOLS_AND_SYSTEMS,
      STATE.CANCELLATION_COMPLETE,
    ],
  },
  {
    name: "Revocation",
    id: [
      STATE.REVOCATION_REVOCATION_REASON,
      STATE.REVOCATION_DRAFT_DECISION,
      STATE.REVOCATION_UNDER_CONSULTATION,
      STATE.REVOCATION_SUBMISSION_RESPONSE,
      STATE.FINALISATION_FINAL_DECISION,
      STATE.REVOCATION_TOOLS_AND_SYSTEMS,
      STATE.REVOCATION_COMPLETE,
    ],
  },
  {
    name: "Withdrawal",
    id: [
      STATE.WITHDRAWAL_WITHDRAWAL_REASON,
      STATE.WITHDRAWAL_INVOICING,
      STATE.WITHDRAWAL_COMPLETE,
    ],
  },
  {
    name: "Variation",
    id: [
      STATE.VARIATION_VARIATION_SO_REASON,
      STATE.VARIATION_VARIATION_AO_REASON,
      STATE.VARIATION_VARIATION_AO_REVIEW,
    ],
  },
  {
    name: "Completed",
    id: [
      STATE.FINALISATION_COMPLETE,
      STATE.CANCELLATION_COMPLETE,
      STATE.REVOCATION_COMPLETE,
      STATE.WITHDRAWAL_COMPLETE,
    ],
  },
]
  .map((item) => {
    if (typeof item.id === "string") {
      return item;
    }
    return { ...item, id: `[${item?.id?.join(",")}]` };
  })
  .sort(sortByName);

export const notificationTypeChoices = [
  {
    name: "Milestone",
    id: "Milestone",
  },
  {
    name: "Task",
    id: "Task",
  },
].sort(sortByName);

export const DEFAULT_DATE_DISPLAY_OPTIONS = {
  day: "2-digit",
  month: "short",
  year: "numeric",
  hour: "numeric",
  minute: "numeric",
  second: "numeric",
  hour12: false,
  variant: "filled",
} as const;

// string enums do not have reverse mapping like normal enum do :(
export function getReverseMap<T extends Record<string, any>, K extends keyof T>(
  type: T,
  value: any
) {
  if (type) {
    const entry = Object.entries(type).find((t) => value === t[1]);
    return entry?.[0] as K;
  }
}

export const formatDate = (date: string) => {
  if (date !== undefined) {
    return format(parseISO(date), DATE_FORMAT);
  }
};

export const consultationDuration = 10;
